import React, { useState, useEffect } from 'react';
import { X, Loader } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const PayPalPaymentModal = ({ isOpen, onClose, amount, onSuccess, appointmentId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paypalLoaded, setPaypalLoaded] = useState(false);

  useEffect(() => {
    if (isOpen) {
      initializePayPal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, amount]);

  const initializePayPal = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get PayPal client ID
      const token = sessionStorage.getItem('token');
      const configResponse = await axios.get(`${API_URL}/payment-settings/active`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const paypalProvider = configResponse.data.providers?.find(p => p.provider === 'paypal');
      
      if (!paypalProvider || !paypalProvider.public_key) {
        throw new Error('PayPal is not configured');
      }

      // Load PayPal SDK
      if (!window.paypal) {
        const script = document.createElement('script');
        script.src = `https://www.paypal.com/sdk/js?client-id=${paypalProvider.public_key}&currency=USD`;
        script.async = true;
        script.onload = () => {
          setPaypalLoaded(true);
          renderPayPalButtons();
        };
        script.onerror = () => {
          setError('Failed to load PayPal SDK');
          setLoading(false);
        };
        document.body.appendChild(script);
      } else {
        setPaypalLoaded(true);
        renderPayPalButtons();
      }

    } catch (err) {
      console.error('Initialize PayPal error:', err);
      setError(err.response?.data?.error || 'Failed to initialize PayPal');
      setLoading(false);
    }
  };

  const renderPayPalButtons = () => {
    if (!window.paypal || !paypalLoaded) return;

    setLoading(false);

    // Clear any existing buttons
    const container = document.getElementById('paypal-button-container');
    if (container) {
      container.innerHTML = '';
    }

    window.paypal.Buttons({
      createOrder: async () => {
        try {
          const token = sessionStorage.getItem('token');
          const response = await axios.post(
            `${API_URL}/payments/create-paypal-order`,
            { amount, appointmentId },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          return response.data.orderID;
        } catch (error) {
          console.error('Create order error:', error);
          setError('Failed to create PayPal order');
          throw error;
        }
      },
      onApprove: async (data) => {
        try {
          const token = sessionStorage.getItem('token');
          const response = await axios.post(
            `${API_URL}/payments/capture-paypal-order`,
            { orderID: data.orderID },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (response.data.success) {
            onSuccess(response.data.captureID);
            onClose();
          } else {
            setError('Payment capture failed');
          }
        } catch (error) {
          console.error('Capture order error:', error);
          setError('Failed to capture payment');
        }
      },
      onError: (err) => {
        console.error('PayPal error:', err);
        setError('Payment failed. Please try again.');
      },
      onCancel: () => {
        // User cancelled - just close modal
        onClose();
      },
      style: {
        layout: 'vertical',
        color: 'blue',
        shape: 'rect',
        label: 'paypal'
      }
    }).render('#paypal-button-container');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-2">
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#003087">
              <path d="M20.905 9.5c.21-1.342.09-2.26-.431-3.126C19.628 5.073 17.816 4.5 15.4 4.5H8.136a.9.9 0 0 0-.889.761L4.69 20.137a.54.54 0 0 0 .533.626h3.88l.974-6.172-.03.194a.9.9 0 0 1 .888-.761h1.85c3.637 0 6.484-1.478 7.316-5.754.025-.127.046-.25.064-.37.243-.114.464-.247.664-.4.21-.16.39-.34.54-.54z"/>
            </svg>
            <h2 className="text-xl font-bold">PayPal Payment</h2>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-4">
            <div className="text-lg font-semibold text-gray-900 mb-2">
              Amount to pay: ${amount.toFixed(2)}
            </div>
          </div>

          {loading && (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader className="animate-spin text-primary-600 mb-4" size={32} />
              <p className="text-gray-600">Loading PayPal...</p>
            </div>
          )}

          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 mb-4">
              <p className="font-semibold mb-2">Error</p>
              <p className="text-sm">{error}</p>
            </div>
          )}

          {!loading && !error && (
            <>
              <div id="paypal-button-container"></div>
              <p className="text-xs text-gray-500 mt-4 text-center">
                You will be redirected to PayPal to complete your payment securely.
              </p>
            </>
          )}

          {error && (
            <button
              onClick={onClose}
              className="mt-4 btn btn-secondary w-full"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PayPalPaymentModal;
